import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePopup } from '../hooks/usePopup';
import { MdOutlineCheckCircle, MdOutlinePayment } from 'react-icons/md';
import { TbSettings2, TbReceipt } from 'react-icons/tb';
import { GoHash } from 'react-icons/go';
import { BsBuilding, BsFilter } from 'react-icons/bs';
import { MdOutlinePeople } from 'react-icons/md';
import { CiUser } from 'react-icons/ci';
import { FaRegFileAlt } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { PiMicrosoftExcelLogoFill } from 'react-icons/pi';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import TaskSheetToolbar from '../components/taskSheet/TaskSheetToolbar';
import TaskStatBox from '../components/taskSheet/TaskStatBox';
import TaskStatusBadge from '../components/taskSheet/TaskStatusBadge';
import TaskRowPopup from '../components/taskSheet/TaskRowPopup';
import DateRangePicker from '../components/ui/DateRangePicker';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchTaskSheetDetail } from '../redux/actions/taskSheetActions';
import { clearTaskSheetDetail } from '../redux/slices/taskSheetSlice';
import { TaskItem } from '../redux/types/taskSheetTypes';
import axiosInstance from '../axios';
import toast from 'react-hot-toast';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

const formatDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const getDefaultDates = () => {
  const now = new Date();
  return {
    from: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDate(now),
  };
};

type StatKey = 'WORKING_ON' | 'DELIVERED' | 'BILLED' | 'PAYMENT_RECEIVED';
const STAT_KEYS: StatKey[] = ['WORKING_ON', 'DELIVERED', 'BILLED', 'PAYMENT_RECEIVED'];
const isStatKey = (v: string | null): v is StatKey => STAT_KEYS.includes(v as StatKey);

const columns = [
  { key: 'mId', header: 'M-ID', icon: <GoHash className="text-xs" />, priority: 5, alwaysVisible: true },
  { key: 'customerName', header: 'Customer Name', icon: <BsBuilding className="text-xs" />, priority: 4 },
  { key: 'projectName', header: 'Project Name', icon: <GoHash className="text-xs" />, priority: 3 },
  { key: 'assignedBy', header: 'Assigned by', icon: <MdOutlinePeople className="text-xs" />, priority: 2 },
  {
    key: 'status',
    header: 'Status',
    icon: <TbSettings2 className="text-xs" />,
    priority: 5,
    alwaysVisible: true,
    renderCell: (value: string) => <TaskStatusBadge status={value} />,
  },
  { key: 'notes', header: 'Notes/Remarks', icon: <MdOutlinePeople className="text-xs" />, priority: 1, truncate: true, maxWidth: '180px' },
];

const TaskSheetDetail = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { employee, stats, projects, pagination, loadingDetail, error } = useAppSelector(s => s.taskSheet);

  const defaults = getDefaultDates();

  // All filter state lives in URL
  const dateFrom     = searchParams.get('dateFrom')      ?? defaults.from;
  const dateTo       = searchParams.get('dateTo')        ?? defaults.to;
  const activeStatKey: StatKey = isStatKey(searchParams.get('status')) ? (searchParams.get('status') as StatKey) : 'WORKING_ON';
  const page         = Number(searchParams.get('page')   ?? 1);
  const limit        = Number(searchParams.get('limit')  ?? 10);
  const customerName = searchParams.get('customerName')  ?? '';
  const articleNo    = searchParams.get('articleNo')     ?? '';

  const setParam = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Initialize missing URL params with defaults on first load
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!next.get('dateFrom')) next.set('dateFrom', defaults.from);
      if (!next.get('dateTo'))   next.set('dateTo',   defaults.to);
      if (!next.get('status'))   next.set('status',   'WORKING_ON');
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search stays local (debounced, transient)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const taskPopup = usePopup();

  const handleExport = useCallback(async () => {
    if (!employeeId) return;
    setExporting(true);
    try {
      const params: Record<string, string> = {
        employee_uid: employeeId,
        dateFrom,
        dateTo,
        status: activeStatKey,
      };
      if (customerName) params.customerName = customerName;
      if (articleNo) params.articleNo = articleNo;
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await axiosInstance.get('/v1/admin/task-sheet/export', {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const name = employee?.fullname?.replace(/\s+/g, '_') ?? 'employee';
      link.download = `tasksheet_${name}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch {
      toast.error('Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [employeeId, dateFrom, dateTo, activeStatKey, customerName, articleNo, debouncedSearch, employee]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(v);
      setParam({ page: '1' });
    }, 350);
  }, [setParam]);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    if (!employeeId) return;
    dispatch(fetchTaskSheetDetail({
      employeeId,
      dateFrom,
      dateTo,
      page,
      limit,
      search: debouncedSearch || undefined,
      status: activeStatKey,
      customerName: customerName || undefined,
      articleNo: articleNo || undefined,
    }));
  }, [dispatch, employeeId, dateFrom, dateTo, page, limit, debouncedSearch, activeStatKey, customerName, articleNo]);

  useEffect(() => () => { dispatch(clearTaskSheetDetail()); }, [dispatch]);

  if (error && !employee) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-red-400 text-xl italic">
        {error}
      </div>
    );
  }

  const totalPages = pagination?.totalPages ?? 0;
  const totalItems = pagination?.total ?? 0;
  const hasActiveFilters = !!(customerName || articleNo);
  const activeFilterCount = [customerName, articleNo].filter(Boolean).length;

  return (
    <div className="flex flex-col w-full gap-5 mt-0 md:mt-8 px-2">
      <div className="w-full p-4 sm:p-8 md:p-10 bg-transparent sm:bg-white rounded-3xl">

        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center ring-2 ring-orange-100 overflow-hidden">
                {employee?.profilePicture ? (
                  <img src={employee.profilePicture} alt={employee.fullname} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-base font-bold">
                    {employee?.fullname?.charAt(0) ?? '?'}
                  </span>
                )}
              </div>
              {employee && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap">
                  {employee.employeeCode}
                </div>
              )}
            </div>
            <div>
              {loadingDetail && !employee ? (
                <div className="space-y-1">
                  <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
                </div>
              ) : (
                <>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{employee?.fullname ?? '—'}</h1>
                  <p className="text-sm text-gray-400 mt-0.5">{employee?.designation ?? '—'}</p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={(from, to) => setParam({ dateFrom: from, dateTo: to, page: '1' })}
            />
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-green-200 bg-white text-sm text-green-600 font-medium hover:bg-green-50 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <PiMicrosoftExcelLogoFill className="text-green-500 text-base flex-shrink-0" />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>

        {/* Stat Boxes — click to filter table */}
        <div className="flex w-full mb-6">
          {loadingDetail && !stats ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-1 h-24 bg-gray-100 rounded-xl mx-1 animate-pulse" />
            ))
          ) : (
            <>
              <TaskStatBox
                icon={<TbSettings2 />} label="Working on"
                count={stats?.workingOnCount ?? 0} amount={stats?.workingOnAmount ?? 0}
                variant="dark"
                isActive={activeStatKey === 'WORKING_ON'}
                onClick={() => setParam({ status: 'WORKING_ON', page: '1' })}
              />
              <TaskStatBox
                icon={<MdOutlineCheckCircle />} label="Delivered"
                count={stats?.deliveredCount ?? 0} amount={stats?.deliveredAmount ?? 0}
                variant="green"
                isActive={activeStatKey === 'DELIVERED'}
                onClick={() => setParam({ status: 'DELIVERED', page: '1' })}
              />
              <TaskStatBox
                icon={<TbReceipt />} label="Billed"
                count={stats?.billedCount ?? 0} amount={stats?.billedAmount ?? 0}
                variant="green"
                isActive={activeStatKey === 'BILLED'}
                onClick={() => setParam({ status: 'BILLED', page: '1' })}
              />
              <TaskStatBox
                icon={<MdOutlinePayment />} label="Payment Received"
                count={stats?.paymentReceivedCount ?? 0} amount={stats?.paymentReceivedAmount ?? 0}
                variant="green"
                isActive={activeStatKey === 'PAYMENT_RECEIVED'}
                onClick={() => setParam({ status: 'PAYMENT_RECEIVED', page: '1' })}
              />
            </>
          )}
        </div>

        {/* Search + Filter row */}
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <TaskSheetToolbar
                search={search}
                dateFrom=""
                dateTo=""
                onSearchChange={handleSearchChange}
                hideDateRange
              />
            </div>
            <button
              onClick={() => setShowFilterPanel(prev => !prev)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-colors flex-shrink-0
                ${showFilterPanel || hasActiveFilters
                  ? 'border-blue-400 text-blue-600 bg-blue-50'
                  : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
                }`}
            >
              <BsFilter className="text-base" />
              Filter
              {hasActiveFilters && (
                <span className="ml-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Filter panel */}
          {showFilterPanel && (
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Customer Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    Company Name
                  </label>
                  <div className="relative">
                    <CiUser className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search company..."
                      className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={customerName}
                      onChange={e => setParam({ customerName: e.target.value || null, page: '1' })}
                    />
                  </div>
                </div>

                {/* Article No */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    Article No
                  </label>
                  <div className="relative">
                    <FaRegFileAlt className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Enter article number..."
                      className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={articleNo}
                      onChange={e => setParam({ articleNo: e.target.value || null, page: '1' })}
                    />
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => setParam({ customerName: null, articleNo: null, page: '1' })}
                    className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-medium"
                  >
                    <IoClose />
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        {loadingDetail && projects.length === 0 ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <ResponsiveTable
            data={projects}
            columns={columns}
            totalItems={totalItems}
            currentPage={page}
            totalPages={totalPages}
            defaultItemsPerPage={limit}
            itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
            onPageChange={p => setParam({ page: String(p) })}
            onItemsPerPageChange={l => setParam({ limit: String(l), page: '1' })}
            onRowClick={(row: TaskItem) => { setSelectedTask(row); taskPopup.openPopup(); }}
            emptyText="No projects found for this period."
          />
        )}
      </div>

      <TaskRowPopup
        task={selectedTask}
        isOpen={taskPopup.isOpen}
        onClose={taskPopup.closePopup}
      />
    </div>
  );
};

export default TaskSheetDetail;
